import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Business Unit (vertical) context.
 *
 * Two verticals live in the same app:
 *   • stretch_film  (legacy — cartons of LLDPE stretch film)
 *   • bopp_tape     (BOPP self-adhesive jumbo rolls)
 *
 * The current vertical is stored in localStorage under BU_KEY and mirrored
 * to an X-Business-Unit header on every API call (see /lib/api.js). The
 * post-login flow routes the user through /select-vertical whenever no
 * vertical has been chosen yet — after that the choice persists across
 * refreshes until they explicitly switch (via the header pill in Layout).
 */

export const BU_KEY = "wpl.business_unit.v1";

export const BUSINESS_UNITS = [
  {
    key: "stretch_film",
    label: "Stretch Film",
    short: "SF",
    tagline: "LLDPE Stretch Film · Cartons",
    accent: "#EA580C",   // orange-600
    accentSoft: "#FFF7ED",
  },
  {
    key: "bopp_tape",
    label: "BOPP Self-Adhesive Tape",
    short: "BOPP",
    tagline: "Jumbo Rolls · Adhesive Tape",
    accent: "#1D4ED8",   // blue-700
    accentSoft: "#EFF6FF",
  },
];

const BusinessUnitContext = createContext(null);

export function BusinessUnitProvider({ children }) {
  const [businessUnit, setBusinessUnit] = useState(() => {
    try {
      const stored = localStorage.getItem(BU_KEY);
      if (BUSINESS_UNITS.some((b) => b.key === stored)) return stored;
    } catch { /* localStorage unavailable */ }
    return null; // null = "not chosen yet" → routing sends to /select-vertical
  });

  const setAndPersist = useCallback((key) => {
    if (!BUSINESS_UNITS.some((b) => b.key === key)) return;
    try { localStorage.setItem(BU_KEY, key); } catch { /* ignore */ }
    setBusinessUnit(key);
  }, []);

  const clear = useCallback(() => {
    try { localStorage.removeItem(BU_KEY); } catch { /* ignore */ }
    setBusinessUnit(null);
  }, []);

  // Sync across tabs — if the user switches vertical in another tab, refresh
  // our state so all screens stay consistent.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === BU_KEY) {
        const v = e.newValue;
        setBusinessUnit(BUSINESS_UNITS.some((b) => b.key === v) ? v : null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const meta = BUSINESS_UNITS.find((b) => b.key === businessUnit) || null;

  return (
    <BusinessUnitContext.Provider value={{ businessUnit, meta, setBusinessUnit: setAndPersist, clear }}>
      {children}
    </BusinessUnitContext.Provider>
  );
}

export function useBusinessUnit() {
  const ctx = useContext(BusinessUnitContext);
  if (!ctx) throw new Error("useBusinessUnit must be used inside <BusinessUnitProvider>");
  return ctx;
}
